import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { Plus, GripVertical, Video, BookOpen, Play, GraduationCap, Pencil, Check, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { Button, Checkbox } from '@/components/shared/ui';
import AddVideoModal from '../class/AddVideoModal';
import AddScenarioModal from './AddScenarioModal';
import { ScenarioDetail } from '../../data/scenarioDetails';
import { ClassItem } from '../../data/classes';
import { CurriculumItem, CurriculumItemType, VideoLecture } from '../../types/curriculum';
import type { ScenarioPlatform } from '../../types/admin';
import { apiPreferredKoEn } from '@/lib/i18nLocale';

/** 단일 소요시간 문자열을 분 단위로 파싱 (예: "7분", "1시간 30분") */
function parseDurationToMinutes(durationStr: string | undefined): number {
    if (!durationStr || !String(durationStr).trim()) return 0;
    const s = String(durationStr);
    let total = 0;
    const hrMatch = s.match(/(\d+)\s*시간/);
    const minMatch = s.match(/(\d+)\s*분/);
    if (hrMatch) total += parseInt(hrMatch[1], 10) * 60;
    if (minMatch) total += parseInt(minMatch[1], 10);
    if (total === 0) {
        const enMin = s.match(/(\d+)\s*min/);
        const enHr = s.match(/(\d+)\s*hr/);
        if (enHr) total += parseInt(enHr[1], 10) * 60;
        if (enMin) total += parseInt(enMin[1], 10);
    }
    if (total === 0) {
        const anyNum = s.match(/(\d+)/);
        if (anyNum) total = parseInt(anyNum[1], 10);
    }
    return total;
}

/** 분 단위를 현재 로케일에 맞는 문자열로 포맷 */
function formatDurationFromMinutes(minutes: number, isEn: boolean): string {
    if (minutes <= 0) return isEn ? '0 min' : '0분';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (isEn) {
        if (hours > 0 && mins > 0) return `${hours} hr ${mins} min`;
        if (hours > 0) return `${hours} hr`;
        return `${mins} min`;
    }
    if (hours > 0 && mins > 0) return `${hours}시간 ${mins}분`;
    if (hours > 0) return `${hours}시간`;
    return `${mins}분`;
}

/** 단일 duration 문자열을 로케일 표시용으로 변환 (예: "7분" -> en이면 "7 min") */
function formatDurationForLocale(durationStr: string | undefined, isEn: boolean): string {
    if (!durationStr) return isEn ? '0 min' : '0분';
    const minutes = parseDurationToMinutes(durationStr);
    return formatDurationFromMinutes(minutes, isEn);
}

/** 시나리오/동영상 data: duration 문자열이 없으면 expectedPlayTimeSeconds로 분 환산 */
function minutesFromSessionData(data: { duration?: string; expectedPlayTimeSeconds?: number } | undefined): number {
    if (!data) return 0;
    const fromStr = parseDurationToMinutes(data.duration);
    if (fromStr > 0) return fromStr;
    const sec = data.expectedPlayTimeSeconds;
    if (typeof sec === 'number' && sec > 0) return Math.ceil(sec / 60);
    return 0;
}

function formatSessionDataDuration(data: { duration?: string; expectedPlayTimeSeconds?: number } | undefined, isEn: boolean): string {
    return formatDurationFromMinutes(minutesFromSessionData(data), isEn);
}

/** 플랫폼 표시용 정규화: "VR, Mobile/PC" 등 복합값을 VR | Mobile | PC 중 하나로 변환 */
function normalizePlatformDisplay(platform: string | undefined): ScenarioPlatform {
    if (!platform) return 'VR';
    const p = platform.trim();
    if (p === 'VR' || p === 'Mobile' || p === 'PC') return p;
    if (p.startsWith('VR') || p.includes('VR,')) return 'VR';
    if (p.startsWith('Mobile') || p === 'Mobile/PC') return 'Mobile';
    if (p.startsWith('PC')) return 'PC';
    return 'VR';
}

/** scenarioDeviceTypes 배열을 표시 문자열로 변환 */
function formatDeviceTypes(deviceTypes: string[] | undefined, platform?: string): string {
    if (deviceTypes && Array.isArray(deviceTypes) && deviceTypes.length > 0) {
        // 배열의 각 값을 표시 (예: ['VR', 'WEB'] -> 'VR, WEB')
        return deviceTypes.join(', ');
    }
    // fallback: platform 사용
    return normalizePlatformDisplay(platform);
}

interface CurriculumSetupProps {
    items: CurriculumItem[];
    onItemsChange: (items: CurriculumItem[]) => void;
    error?: string;
    /** 구매한 프로덕트 목록 (카드 클릭 시 해당 프로덕트 커리큘럼 불러옴) */
    purchasedProducts?: ClassItem[];
    /** true면 편집 완료/편집 버튼 숨김, 항상 편집 UI(시나리오·동영상 추가) 표시 */
    hideEditCompleteAndReset?: boolean;
    /** 부모에서 편집 모드 제어 시 사용. 있으면 내부 편집/편집완료 버튼 숨김 */
    editMode?: boolean;
    onEditModeChange?: (value: boolean) => void;
}

export default function CurriculumSetup({
    items,
    onItemsChange,
    error,
    purchasedProducts = [],
    hideEditCompleteAndReset = false,
    editMode: controlledEditMode,
    onEditModeChange
}: CurriculumSetupProps): React.ReactElement {
    const { t, i18n } = useTranslation('class');
    const isEn = apiPreferredKoEn(i18n.language) === 'en';
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [isScenarioModalOpen, setIsScenarioModalOpen] = useState(false);
    /** 프로덕트 선택 전: 프로덕트 선택 섹션만 표시. 선택 후: 커리큘럼 조회/편집 */
    const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
    /** 커리큘럼 편집 모드: false = 조회(읽기 전용), true = 편집. 부모 제어 시 editMode 사용 */
    const [isEditMode, setIsEditMode] = useState(false);
    const effectiveEditMode = hideEditCompleteAndReset || (controlledEditMode !== undefined ? controlledEditMode : isEditMode);
    const showInternalEditButtons = !hideEditCompleteAndReset && onEditModeChange == null;
    /** 세션 선택 체크박스: 선택된 세션 ID 목록 */
    const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
    /** 세션 클릭 시 아래줄에 시나리오 소개/동영상 설명 표시 */
    const [expandedSessionId, setExpandedSessionId] = useState<string | null>(null);

    const showProductSelection = purchasedProducts.length > 0 && selectedProductId === null;

    /** 비어 있는 첫 행 제외: 제목이 없는 첫 항목은 목록 조회 시 제외 */
    const displayItems = useMemo(() => {
        const list = items || [];
        if (list.length === 0) return list;
        const first = list[0];
        const firstTitle = (first?.data as { title?: string })?.title;
        if (!firstTitle || !String(firstTitle).trim()) return list.slice(1);
        return list;
    }, [items]);

    const itemIdsKey = useMemo(() => (items?.length ? [...items].map((i) => i.id).sort().join(',') : ''), [items]);
    /** 비어 있는 첫 항목이 있으면 부모 state에서 제거 (동기 업데이트는 DnD 커밋과 겹치지 않게 한 틱 뒤로) */
    useEffect(() => {
        const list = items || [];
        if (list.length === 0) return;
        const firstTitle = (list[0]?.data as { title?: string })?.title;
        if (!firstTitle || !String(firstTitle).trim()) {
            const next = list.slice(1);
            const t = window.setTimeout(() => {
                onItemsChange(next);
            }, 0);
            return () => window.clearTimeout(t);
        }
        return undefined;
        // itemIdsKey만 추적: 항목 집합이 바뀔 때만 재검사 (onItemsChange 참조 변화로 타이머 누적 방지)
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally
    }, [itemIdsKey]);

    useEffect(() => {
        if (items?.length) {
            setSelectedSessionIds(new Set(items.map((i) => i.id)));
        } else {
            setSelectedSessionIds(new Set());
        }
    }, [itemIdsKey]);

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination || !displayItems.length) return;

        const newItems = Array.from(displayItems);
        const [reorderedItem] = newItems.splice(result.source.index, 1);
        newItems.splice(result.destination.index, 0, reorderedItem);

        onItemsChange(newItems);
    };

    const handleAddVideo = (video: VideoLecture) => {
        if (!video) return;
        const newItem: CurriculumItem = {
            id: video.id,
            type: 'video',
            data: video
        };
        onItemsChange([...(items || []), newItem]);
    };

    const handleAddScenarios = (scenarios: ScenarioDetail[]) => {
        if (!scenarios?.length) return;
        const existingIds = new Set(selectedScenarioIds);
        const toAdd = scenarios.filter((d) => !existingIds.has(d.id));
        if (!toAdd.length) return;
        const newItems: CurriculumItem[] = toAdd.map((detail) => ({
            id: `scenario-${detail.id}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            type: 'scenario' as CurriculumItemType,
            data: detail,
        }));
        onItemsChange([...(items || []), ...newItems]);
    };

    const handleDeleteSelected = () => {
        if (!selectedSessionIds.size || !items?.length) return;
        const nextItems = items.filter((item) => !selectedSessionIds.has(item.id));
        onItemsChange(nextItems);
        setSelectedSessionIds(new Set());
    };

    const selectedScenarioIds = useMemo(() => (items || [])
        .filter((i): i is CurriculumItem & { type: 'scenario' } => i?.type === 'scenario')
        .map((i) => {
            const d = i.data as ScenarioDetail & { scenarioId?: number };
            return d.scenarioId ?? d.id;
        })
        .filter((id): id is number => id != null), [items]);

    /** 프로덕트 커리큘럼을 폼용 CurriculumItem[]로 변환 */
    const mapProductCurriculumToItems = (product: ClassItem): CurriculumItem[] => {
        const rawCurriculum = product.curriculum || [];
        return rawCurriculum.map((entry) => {
            const itemId = `product-${product.id}-${entry.id}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            if (entry.type === 'video') {
                return {
                    id: itemId,
                    type: 'video',
                    data: {
                        id: String(entry.id),
                        title: entry.name,
                        url: (entry as { url?: string }).url || '',
                        duration: entry.duration,
                        author: {
                            name: entry.author || 'Unknown',
                            type: entry.authorType || 'individual'
                        },
                        description: ''
                    } as VideoLecture
                };
            }
            // API 데이터만 사용
            return {
                id: itemId,
                type: 'scenario',
                data: {
                    id: entry.id,
                    title: entry.name,
                    duration: entry.duration,
                    platform: entry.platform,
                    ContributedBy: entry.author || 'Medicrew'
                } as ScenarioDetail
            };
        });
    };

    const handleLoadProductCurriculum = (product: ClassItem) => {
        const mapped = mapProductCurriculumToItems(product);
        const existingScenarioIds = (items || [])
            .filter((i) => i?.type === 'scenario')
            .map((i) => (i?.data as ScenarioDetail)?.id);
        const existingVideoIds = (items || [])
            .filter((i) => i?.type === 'video')
            .map((i) => (i?.data as VideoLecture)?.id);
        const unique = mapped.filter((item) => {
            if (item.type === 'scenario') {
                return !existingScenarioIds.includes((item.data as ScenarioDetail)?.id);
            }
            return !existingVideoIds.includes((item.data as VideoLecture)?.id);
        });
        onItemsChange([...(items || []), ...unique]);
        setSelectedProductId(product.id);
    };

    const calculateTotalDuration = (): string => {
        const totalMinutes = (items || []).reduce(
            (sum, item) => sum + minutesFromSessionData(item?.data as { duration?: string; expectedPlayTimeSeconds?: number }),
            0
        );
        return formatDurationFromMinutes(totalMinutes, isEn);
    };

    return (
        <div>
            {/* 프로덕트 선택 이전: 섹션명 "프로덕트 선택", 프로덕트 카드 목록만 표시 */}
            {showProductSelection && (
                <>
                    <h3 className="text-sm font-medium text-gray-600 py-2">{t('curriculumSetup.productSelect')}</h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {purchasedProducts.map((product) => (
                            <button
                                key={product.id}
                                type="button"
                                onClick={() => handleLoadProductCurriculum(product)}
                                className="flex flex-col rounded-lg border border-gray-200 bg-white p-3 text-left shadow-sm transition-shadow hover:shadow-md hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                            >
                                <div className="aspect-video w-full rounded-md bg-gray-100 overflow-hidden mb-2">
                                    {product.thumbnail ? (
                                        <img src={product.thumbnail} alt={product.title} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                            <GraduationCap size={28} />
                                        </div>
                                    )}
                                </div>
                                <span className="text-sm font-medium text-gray-900 line-clamp-2">{product.title}</span>
                            </button>
                        ))}
                    </div>
                </>
            )}

            {/* 프로덕트 선택 후: 커리큘럼 조회(읽기) 또는 커리큘럼 편집 */}
            {!showProductSelection && (
                <>
                    <div className="flex items-center justify-between py-2">
                        <div className="flex items-center ml-2.5 gap-4">
                            {effectiveEditMode && displayItems.length > 0 && (
                                <Checkbox
                                    checked={displayItems.length > 0 && displayItems.every((i) => selectedSessionIds.has(i.id))}
                                    onCheckedChange={(checked) => {
                                        setSelectedSessionIds(checked ? new Set(displayItems.map((i) => i.id)) : new Set());
                                    }}
                                    className="flex-shrink-0"
                                />
                            )}
                            <div className="flex items-center gap-3 text-sm font-semibold text-gray-500">
                                <span>{t('curriculumSetup.totalLabel')}: <span className="text-brand-600">{(items?.length) || 0}</span></span>
                                <span className="text-gray-400">|</span>
                                <span>{t('curriculumSetup.totalDuration')}: <span className="text-brand-600">{calculateTotalDuration()}</span></span>
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {effectiveEditMode && (
                                <>
                                    <Button variant="outline" size="sm" onClick={() => setIsScenarioModalOpen(true)}>
                                        <Plus size={16} className="mr-1" />
                                        {t('curriculumSetup.addScenario')}
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => setIsVideoModalOpen(true)}>
                                        <Plus size={16} className="mr-1" />
                                        {t('curriculumSetup.addVideo')}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        disabled={selectedSessionIds.size === 0}
                                        onClick={handleDeleteSelected}
                                    >
                                        <Trash2 size={16} className="mr-1" />
                                        {t('curriculumSetup.deleteSelected')}
                                    </Button>
                                </>
                            )}
                            {showInternalEditButtons && (
                                <>
                                    {isEditMode ? (
                                        <Button variant="lightdark" size="sm" onClick={() => setIsEditMode(false)}>
                                            <Check size={16} className="mr-1" />
                                            {t('curriculumSetup.editComplete')}
                                        </Button>
                                    ) : (
                                        <Button variant="lightdark" size="sm" onClick={() => setIsEditMode(true)}>
                                            <Pencil size={16} className="mr-1" />
                                            {t('curriculumSetup.edit')}
                                        </Button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {error && (
                        <p className="text-sm text-red-600 mb-4">{error}</p>
                    )}

                    {effectiveEditMode ? (
                        <DragDropContext key={itemIdsKey || 'empty'} onDragEnd={handleDragEnd}>
                            <Droppable droppableId="curriculum">
                                {(provided, snapshot) => (
                                    <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="space-y-1"
                                    >
                                        {displayItems.map((item, index) => (
                                            <Draggable
                                                key={item.id}
                                                draggableId={item.id}
                                                index={index}
                                            >
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        className={`bg-white border border-gray-200 rounded-lg p-2.5 transition-shadow ${snapshot.isDragging ? 'shadow-md ring-1 ring-blue-100' : 'hover:shadow-md'}`}
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            {/* 세션 선택 체크박스 */}
                                                            <Checkbox
                                                                checked={selectedSessionIds.has(item.id)}
                                                                onCheckedChange={(checked) => {
                                                                    setSelectedSessionIds((prev) => {
                                                                        const next = new Set(prev);
                                                                        if (checked) next.add(item.id);
                                                                        else next.delete(item.id);
                                                                        return next;
                                                                    });
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="flex-shrink-0"
                                                            />
                                                            {/* Drag Handle */}
                                                            <div
                                                                {...provided.dragHandleProps}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
                                                            >
                                                                <GripVertical size={16} />
                                                            </div>

                                                            {/* Order Number */}
                                                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-white text-brand-600 flex items-center justify-center font-semibold text-xs">
                                                                {index + 1}
                                                            </div>

                                                            {/* Item Info - 세션 클릭 시 아래줄에 시나리오 소개/동영상 설명 표시 */}
                                                            <button
                                                                type="button"
                                                                onClick={() => setExpandedSessionId((prev) => (prev === item.id ? null : item.id))}
                                                                className="flex-1 min-w-0 text-left rounded focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                                                            >
                                                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                                    {/* Title and Badge - 플랫폼 배지를 세션명 앞에 표시 */}
                                                                    <div className="flex items-center gap-2 min-w-0">
                                                                        <span className={`flex-shrink-0 min-w-14 text-xs text-center rounded-md font-medium py-0.5 px-1 ${item.type === 'scenario' ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-600'}`}>
                                                                            {item.type === 'scenario'
                                                                                ? formatDeviceTypes((item.data as ScenarioDetail).scenarioDeviceTypes, (item.data as ScenarioDetail).platform)
                                                                                : t('detail.video')}
                                                                        </span>
                                                                        <h4 className="text-sm font-semibold text-gray-800 truncate">
                                                                            {item.data.title}
                                                                        </h4>
                                                                    </div>

                                                                    {/* Metadata - Right Aligned */}
                                                                    <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-2">
                                                                        {/* 저작자 */}
                                                                        <div className="flex items-center justify-end">
                                                                            <span className="w-max-60 text-xs text-right text-gray-700 font-medium">{item.type === 'scenario' ? (
                                                                                (item.data as ScenarioDetail).ContributedBy
                                                                            ) : (
                                                                                (item.data as VideoLecture).author.name
                                                                            )}</span>
                                                                        </div>
                                                                        {/* 소요시간 */}
                                                                        <div className="flex items-center justify-end">
                                                                            <span className="w-10 text-xs text-left text-brand-600 font-medium">{formatSessionDataDuration(item.data as { duration?: string; expectedPlayTimeSeconds?: number }, isEn)}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </button>
                                                            {/* Chevron - 맨 우측 */}
                                                            <button
                                                                type="button"
                                                                onClick={(e) => { e.stopPropagation(); setExpandedSessionId((prev) => (prev === item.id ? null : item.id)); }}
                                                                className="flex-shrink-0 p-1 rounded text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                                                            >
                                                                {expandedSessionId === item.id ? (
                                                                    <ChevronUp size={16} />
                                                                ) : (
                                                                    <ChevronDown size={16} />
                                                                )}
                                                            </button>
                                                        </div>
                                                        {/* 세션 클릭 시 아래줄: 시나리오 소개 / 동영상 설명 */}
                                                        {expandedSessionId === item.id && (
                                                            <div className="mt-3 px-8 py-2 border-t border-gray-100">
                                                                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                                    {item.type === 'scenario'
                                                                        ? (item.data as ScenarioDetail).handover || (item.data as ScenarioDetail).title || t('curriculumSetup.noIntro')
                                                                        : (item.data as VideoLecture).description || (item.data as VideoLecture).title || t('curriculumSetup.noDescription')}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>
                    ) : (
                        <div className="space-y-1">
                            {displayItems.map((item, index) => (
                                <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-2.5 hover:shadow-md transition-shadow">
                                    <div className="flex items-center gap-3">
                                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-semibold text-xs">
                                            {index + 1}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setExpandedSessionId((prev) => (prev === item.id ? null : item.id))}
                                            className="flex-1 min-w-0 text-left rounded focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                                        >
                                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className={`flex-shrink-0 min-w-14 text-xs text-center rounded-md font-medium py-0.5 px-1 ${item.type === 'scenario' ? 'bg-brand-100 text-brand-600' : 'bg-gray-100 text-gray-600'}`}>
                                                        {item.type === 'scenario'
                                                            ? formatDeviceTypes((item.data as ScenarioDetail).scenarioDeviceTypes, (item.data as ScenarioDetail).platform)
                                                            : t('detail.video')}
                                                    </span>
                                                    <h4 className="text-sm font-semibold text-gray-800 truncate">{item.data.title}</h4>
                                                </div>
                                                <div className="flex flex-wrap items-center justify-end gap-x-5 gap-y-2">
                                                    <span className="w-max-60 text-xs text-right text-gray-700 font-medium">
                                                        {item.type === 'scenario' ? (item.data as ScenarioDetail).ContributedBy : (item.data as VideoLecture).author.name}
                                                    </span>
                                                    <span className="w-10 text-xs text-left text-brand-600 font-medium">{formatSessionDataDuration(item.data as { duration?: string; expectedPlayTimeSeconds?: number }, isEn)}</span>
                                                </div>
                                            </div>
                                        </button>
                                        {/* Chevron - 맨 우측 */}
                                        <button
                                            type="button"
                                            onClick={() => setExpandedSessionId((prev) => (prev === item.id ? null : item.id))}
                                            className="flex-shrink-0 p-1 rounded text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
                                        >
                                            {expandedSessionId === item.id ? (
                                                <ChevronUp size={16} />
                                            ) : (
                                                <ChevronDown size={16} />
                                            )}
                                        </button>
                                    </div>
                                    {/* 세션 클릭 시 아래줄: 시나리오 소개 / 동영상 설명 */}
                                    {expandedSessionId === item.id && (
                                        <div className="mt-3 px-9 py-2 border-t border-gray-100">
                                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                {item.type === 'scenario'
                                                    ? (item.data as ScenarioDetail).handover || (item.data as ScenarioDetail).title || t('curriculumSetup.noIntro')
                                                    : (item.data as VideoLecture).description || (item.data as VideoLecture).title || t('curriculumSetup.noDescription')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                </>
            )}

            <AddVideoModal
                isOpen={isVideoModalOpen}
                onClose={() => setIsVideoModalOpen(false)}
                onAdd={handleAddVideo}
            />
            <AddScenarioModal
                isOpen={isScenarioModalOpen}
                onClose={() => setIsScenarioModalOpen(false)}
                onSelect={handleAddScenarios}
                selectedScenarioIds={selectedScenarioIds}
            />
        </div>
    );
}
